import { RTW_INTRODUCTION_DATE } from "./helpers";
import history from "../history";

const STEP1 = "registration/personal-details";

const isWorkHistoryCompleted = (appData) => {
  const workHistory = appData?.workHistory || [];
  return workHistory.length > 0;
};

const isCvCompleted = (appData) => {
  const cv = appData?.cv || null;
  return !!cv;
};

const hasAddress = (appData) => {
  const address = appData?.address || null;
  const { addressLine1, town, postcode, county, country } = address || {};
  return !!addressLine1 && !!town && !!postcode && !!county && !!country;
};

const isPersonalDetailsCompleted = (appData) => {
  return (
    !!appData?.firstName &&
    !!appData?.lastName &&
    !!appData?.dateOfBirth &&
    !!appData?.mobileTelephone &&
    !!hasAddress(appData)
  );
};

const isProfilePictureCompleted = (appData) => {
  return (
    !!appData?.profileImage &&
    (!!appData?.profileImage.previewContent || !!appData?.profileImage.url)
  );
};

const isTypeOfWorkIsCompleted = (appData) => {
  return (
    !!appData?.positionSeeking &&
    appData?.driver !== undefined &&
    appData?.ownCar !== undefined
  );
};

const isEmergencyContactCompleted = (appData) => {
  return !!(appData.emergencyContactName && appData.emergencyContactTelephone);
};

const isDemographicsCompleted = (appData) => {
  return !!(
    appData.nationalityCode &&
    appData.gender &&
    appData.sexualOrientationCode &&
    appData.ethnicityCode
  );
};

const isWorkingWeekOptOutCompleted = (appData) => {
  return appData?.wtr48HrWaiver !== undefined;
};

const isHealthAndDisabilityQuestionCompleted = (appData) => {
  return appData?.healthAndDisability !== undefined;
};

const isHealthAndDisabilityNoteCompleted = (appData) => {
  if (appData?.healthAndDisability) {
    return !!appData?.healthAndDisabilityNote;
  }
  return isHealthAndDisabilityQuestionCompleted(appData);
};

const isHealthAndDisabilityCompleted = (appData) => {
  return (
    isHealthAndDisabilityQuestionCompleted(appData) &&
    isHealthAndDisabilityNoteCompleted(appData)
  );
};

const hasContractForServices = (appData) => {
  return !!appData?.hasCFS;
};

const isRTWCompleted = (appData) => {
  return (
    appData.rightToWorkVerified ||
    appData.rightToWorkVerifiedInFONT ||
    (appData.rightToWorkChecked &&
      new Date(appData.createdAt) < RTW_INTRODUCTION_DATE)
  );
};

const navigateTo = (id, step) =>
  history.push(`/profile/edit/job/${id}/${step}`);

const navigateToStepOne = (id) => navigateTo(id, STEP1);

const returnFirstNonCompletedStep = (id, appData) => {
  if (!isPersonalDetailsCompleted(appData)) {
    return `/profile/edit/job/${id}/${STEP1}`;
  }

  if (!isProfilePictureCompleted(appData)) {
    return `/profile/edit/job/${id}/registration/profile-picture`;
  }

  if (!isTypeOfWorkIsCompleted(appData)) {
    return `/profile/edit/job/${id}/registration/type-of-work`;
  }

  if (!isWorkHistoryCompleted(appData) && !isCvCompleted(appData)) {
    return `/profile/edit/job/${id}/registration/cv`;
  }

  if (!isEmergencyContactCompleted(appData)) {
    return `/profile/edit/job/${id}/registration/emergency-contact`;
  }

  if (!isDemographicsCompleted(appData)) {
    return `/profile/edit/job/${id}/registration/demographics`;
  }

  if (!isWorkingWeekOptOutCompleted(appData)) {
    return `/profile/edit/job/${id}/legal-compliance/48-hour-opt-out`;
  }

  if (!isHealthAndDisabilityCompleted(appData)) {
    return `/profile/edit/job/${id}/legal-compliance/health-and-disability`;
  }

  if (!hasContractForServices(appData)) {
    return `/profile/edit/job/${id}/legal-compliance/contract-for-services`;
  }

  if (!isRTWCompleted(appData)) {
    return `/profile/edit/job/${id}/legal-compliance/rtw`;
  }

  const currentJob = appData.candidateJobs.find((job) => job.id === id);

  if (!currentJob?.sections) {
    // return `/profile/edit/job/${id}/${STEP1}`;
    return null;
  }

  return Object.entries(currentJob.sections).reduce(
    (acc, [sectionUrl, { forms }]) => {
      if (acc) return acc;
      const nonCompletedForm = forms.find((form) => !form.completed);
      return nonCompletedForm
        ? `/profile/edit/job/${id}/${sectionUrl}/${nonCompletedForm.url}`
        : null;
    },
    null
  );
};

const navigateToFirstNonCompletedStep = (id, appData) => {
  const firstNonCompletedUrl = returnFirstNonCompletedStep(id, appData);
  return firstNonCompletedUrl
    ? history.push(firstNonCompletedUrl)
    : navigateToStepOne(id);
};

const isRegistrationCompleted = (appData) => {
  return (
    (isWorkHistoryCompleted(appData) || isCvCompleted(appData)) &&
    isPersonalDetailsCompleted(appData) &&
    isProfilePictureCompleted(appData) &&
    isTypeOfWorkIsCompleted(appData) &&
    isEmergencyContactCompleted(appData) &&
    isDemographicsCompleted(appData)
  );
};

const isLegalComplianceCompleted = (appData) => {
  return (
    isWorkingWeekOptOutCompleted(appData) &&
    isHealthAndDisabilityCompleted(appData) &&
    hasContractForServices(appData) &&
    isRTWCompleted(appData)
  );
};

export {
  isRegistrationCompleted,
  isLegalComplianceCompleted,
  navigateToStepOne,
  navigateToFirstNonCompletedStep,
  isPersonalDetailsCompleted,
  isProfilePictureCompleted,
  isWorkHistoryCompleted,
  isCvCompleted,
  isTypeOfWorkIsCompleted,
  isEmergencyContactCompleted,
  isDemographicsCompleted,
  isWorkingWeekOptOutCompleted,
  isHealthAndDisabilityCompleted,
  hasContractForServices,
  isRTWCompleted,
  returnFirstNonCompletedStep,
};
